import { HttpContext } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { from, Observable, of } from 'rxjs'
import { switchMap, tap } from 'rxjs/operators'
import { v4 as uuidv4 } from 'uuid'
import { DrrHttpClient } from '../../http/drr-http-client'
import { VehicleAdapter } from '../../shared/adapters/VehicleAdapter'
import { INCLUDE_AUTH_HEADERS } from '../../shared/services/http-context.tokens'
import { BaseAuthRequestParams, NameAuthRequestParams, } from '../auth/auth.types'
import { RapService } from '../rap/rap.service'
import { AddVehicleResponse, MemberInfo, MemberVehicles, SearchMemberResult, Vehicle, VehicleData, } from './member.types'
import { MemberSchema, MemberVehiclesSchema, SearchMemberResultSchema } from './members.model'


@Injectable({ providedIn: 'root' })
export class MemberService {
  constructor(
    private http: DrrHttpClient,
    private rapService: RapService,
    private vehicleAdapter: VehicleAdapter,
  ) {}

  getInfo$(): Observable<MemberInfo> {
    return this.http.get<MemberInfo>('bff/member/details').pipe(
      switchMap((response) => from(MemberSchema.validate(response)))
    )
  }

  getVehicles$(): Observable<MemberVehicles> {
    return this.http.get<MemberVehicles>('bff/member/profile/vehicles').pipe(
      switchMap((response) => from(MemberVehiclesSchema.validate(response)))
    )
  }

  addVehicle$(data: VehicleData): Observable<AddVehicleResponse> {
    if(this.rapService.isRapUser()) { // RAP's backend doesn't have capability to store a vehicle.
      return of({ id: uuidv4() })
    }

    return this.http.post<AddVehicleResponse>(
      'bff/member/profile/vehicles',
      this.vehicleAdapter.adaptMemberVehicle(data)
    )
  }

  updateVehicle$(data: Vehicle): Observable<void> {
    return this.http.put<void>(
      `bff/member/profile/vehicles/${data.id}`,
      this.vehicleAdapter.adaptMemberVehicle(data)
    )
  }

  deleteVehicle$(id: string): Observable<void> {
    return this.http.delete<void>(`bff/member/profile/vehicles/${id}`)
  }

  memberSearch$({
    captchaVersion,
    captchaToken,
    firstName,
    lastName,
    zipCode,
  }: BaseAuthRequestParams & NameAuthRequestParams) {
    const body = {
      captchaVersion,
      captchaToken,
      firstName,
      lastName,
      zipCode,
    }

    return this.http.post<SearchMemberResult>('bff/member/search', body, {
      context: new HttpContext().set(INCLUDE_AUTH_HEADERS, false),
    }).pipe(
      tap((response) => {
        SearchMemberResultSchema.validateSync(response)
      })
    )
  }
}
